import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { MissionBundle, Role } from '@apos/shared';
import { Button, Chip, Field, Icon, Spinner, TextInput, toast } from '../../components/ui';
import { api, errorMessage, isApiError } from '../../lib/api';
import { fmt } from '../../lib/fmt';
import { kindLabel, KIND_ICON, ROLE_LABEL, STATUS_LABEL, STATUS_TONE } from '../labels';
import { AppHeader } from '../AppHeader';
import { Notice } from './pageParts';

/** What each invite role can do, in the crew's words. */
const ROLE_CAN: Record<Role, string> = {
  owner: 'You own this shoot: plan it, invite crew and run it live.',
  producer: 'You can edit the plan, invite crew and run the shoot live.',
  crew: 'You can mark moments, message the team and share your position on the day.',
  viewer: 'You can follow the shoot live and in replay. The plan is read-only for you.',
};
/** How long the welcome card stays before the shoot opens on its own. */
const WELCOME_MS = 1600;

const destination = (b: MissionBundle) => `/m/${b.mission.id}/${b.mission.status === 'live' ? 'live' : b.mission.status === 'ended' ? 'replay' : 'plan'}`;

export function JoinPage() {
  const params = useParams();
  const nav = useNavigate();
  const [code, setCode] = useState((params.code ?? '').toUpperCase());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState<MissionBundle | null>(null);
  const opened = useRef(false);

  const open = (bundle: MissionBundle) => {
    if (opened.current) return;
    opened.current = true;
    toast({ title: `You’re on “${bundle.mission.name}”`, body: `${ROLE_LABEL[bundle.myRole]} access`, tone: 'success', timeoutMs: 4000 });
    nav(destination(bundle), { replace: true });
  };

  const join = async (c: string) => {
    setBusy(true);
    setError(null);
    try {
      // codes are letters and digits; a dash or spaces typed from the "XXXX-XXXX" hint are ignored
      setJoined(await api.join(c.replace(/[\s-]/g, '')));
    } catch (e) {
      setError(isApiError(e) && e.code === 'invalid_invite' ? `${errorMessage(e)}. Ask the producer for a new invite link.` : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // a short welcome, then the shoot opens by itself (the button opens it straight away)
  useEffect(() => {
    if (!joined) return;
    const t = setTimeout(() => open(joined), WELCOME_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined]);

  // join once per link (StrictMode / re-renders must not redeem it twice or toast twice)
  const tried = useRef<string | null>(null);
  useEffect(() => {
    if (params.code && tried.current !== params.code) {
      tried.current = params.code;
      void join(params.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.code]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.trim()) void join(code);
  };

  const redeemingLink = !!params.code && busy && !error && !joined;

  return (
    <div className="page">
      <AppHeader back={{ to: '/missions', label: 'Back to shoots' }} />
      <main className="page__main page__main--narrow join-page">
        {joined ? (
          <section className="join-welcome" aria-live="polite">
            <div className="join-welcome__mark" aria-hidden>
              <Icon name="check" size={26} strokeWidth={2} />
            </div>
            <div className="eyebrow">You’re on the crew</div>
            <h1 className="join-welcome__title">{joined.mission.name}</h1>
            <div className="join-welcome__meta">
              <Chip size="sm" tone={STATUS_TONE[joined.mission.status]} dot={joined.mission.status === 'live'}>
                {STATUS_LABEL[joined.mission.status]}
              </Chip>
              <span>
                <Icon name={KIND_ICON[joined.mission.kind]} size={14} /> {kindLabel(joined.mission.kind)}
              </span>
              <span className="num">
                {fmt.date(joined.mission.windowStart, joined.mission.timezone)} · {fmt.local(joined.mission.windowStart, joined.mission.timezone, false)}
              </span>
            </div>
            <div className="join-welcome__role">
              <Chip size="sm" tone="accent">
                {ROLE_LABEL[joined.myRole]}
              </Chip>
              <p>{ROLE_CAN[joined.myRole]}</p>
            </div>
            <Button variant="primary" size="lg" iconRight="arrow-right" onClick={() => open(joined)}>
              Open shoot
            </Button>
            <div className="join-welcome__progress" aria-hidden>
              <span style={{ animationDuration: `${WELCOME_MS}ms` }} />
            </div>
          </section>
        ) : redeemingLink ? (
          <section className="join-welcome" role="status">
            <Spinner size={28} />
            <div className="eyebrow">Invite link</div>
            <h1 className="join-welcome__title">Joining shoot…</h1>
            <p className="muted">Checking your invite with the producer’s shoot.</p>
          </section>
        ) : (
          <>
            <div className="page__head">
              <div>
                <div className="eyebrow">Join</div>
                <h1>Join a shoot</h1>
                <p className="page__lede">Enter the invite code from the shoot’s producer. What you can change (crew, producer or viewer) comes from the invite.</p>
              </div>
            </div>
            <form className="form-card join-form" onSubmit={submit} noValidate>
              <Field label="Invite code" error={error ?? undefined} hint="8 letters and digits. Dashes and spaces are ignored.">
                {(id) => (
                  <TextInput
                    id={id}
                    mono
                    className="input--code"
                    autoFocus
                    autoCapitalize="characters"
                    autoComplete="off"
                    aria-invalid={error ? true : undefined}
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.toUpperCase());
                      if (error) setError(null);
                    }}
                    placeholder="XXXX-XXXX"
                  />
                )}
              </Field>
              <div className="form-actions">
                <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!code.trim()} iconRight="arrow-right">
                  Join shoot
                </Button>
              </div>
            </form>
            <Notice className="join-help" icon="link">
              Got a link instead? Open it on this device and you’ll join straight away.
            </Notice>
          </>
        )}
      </main>
    </div>
  );
}
